import React, { useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useTheme } from '@mui/material/styles';
import type Supercluster from 'supercluster';

interface ClusteringGridOverlayProps {
  clusteringOptions?: Supercluster.Options<GeoJSON.GeoJsonProperties, Record<string, unknown>>;
  visible?: boolean;
}

/**
 * Component that visualizes the clustering grid/tile system used by Supercluster
 * This helps understand how markers are grouped into clusters based on the extent parameter
 */
const ClusteringGridOverlay: React.FC<ClusteringGridOverlayProps> = ({ clusteringOptions, visible = false }) => {
  const map = useMap();
  const theme = useTheme();
  const rectanglesRef = useRef<google.maps.Rectangle[]>([]);

  useEffect(() => {
    if (!map || !visible || !clusteringOptions) {
      // Clear all rectangles when hidden
      rectanglesRef.current.forEach((rect) => rect.setMap(null));
      rectanglesRef.current = [];
      return;
    }

    const extent = clusteringOptions.extent ?? 512;
    const minZoom = clusteringOptions.minZoom ?? 0;
    const maxZoom = clusteringOptions.maxZoom ?? 20;

    // Get current map state
    const zoom = Math.floor(map.getZoom() ?? 10);
    const bounds = map.getBounds();

    if (!bounds || zoom < minZoom || zoom > maxZoom) {
      rectanglesRef.current.forEach((rect) => rect.setMap(null));
      rectanglesRef.current = [];
      return;
    }

    // Supercluster uses a tile system based on Web Mercator projection
    // At zoom level z, there are 2^z standard tiles (256px each)
    // The extent parameter scales the tile size
    // Standard tile size calculation for Web Mercator
    const numStandardTiles = Math.pow(2, zoom);

    // Calculate standard tile size in degrees (longitude is uniform, latitude varies)
    // For longitude: 360 / 2^z
    const standardTileSizeLng = 360 / numStandardTiles;

    // Scale tile size based on extent (extent is typically 256, 512, or 1024)
    // Larger extent = larger tiles (more markers per tile)
    const tileSizeLng = (standardTileSizeLng * extent) / 256;

    // For latitude, we need to account for Web Mercator projection
    // But for visualization purposes, we'll use a simplified approach
    // that approximates the tile boundaries
    const tileSizeLat = tileSizeLng; // Approximate as square tiles for visualization

    // Calculate tile boundaries for the entire world map
    // Use full world bounds: -180 to 180 longitude, -90 to 90 latitude
    const minLng = -180;
    const maxLng = 180;
    const minLat = -90;
    const maxLat = 90;

    // Clear existing rectangles
    rectanglesRef.current.forEach((rect) => rect.setMap(null));
    rectanglesRef.current = [];

    // Create rectangles for each tile covering the entire map
    // Calculate total number of tiles to determine if we need to limit rendering
    const tilesLng = Math.ceil((maxLng - minLng) / tileSizeLng);
    const tilesLat = Math.ceil((maxLat - minLat) / tileSizeLat);
    const totalTiles = tilesLng * tilesLat;

    // For performance, limit rendering at very high zoom levels
    // At lower zoom levels, show all tiles; at higher zoom, limit to reasonable number
    const maxTiles = zoom > 12 ? 5000 : totalTiles;

    let tileCount = 0;

    for (let lng = minLng; lng < maxLng && tileCount < maxTiles; lng += tileSizeLng) {
      for (let lat = minLat; lat < maxLat && tileCount < maxTiles; lat += tileSizeLat) {
        // Ensure bounds are valid
        const north = Math.min(lat + tileSizeLat, 90);
        const south = Math.max(lat, -90);
        const east = Math.min(lng + tileSizeLng, 180);
        const west = Math.max(lng, -180);

        const rectangle = new google.maps.Rectangle({
          bounds: new google.maps.LatLngBounds(new google.maps.LatLng(south, west), new google.maps.LatLng(north, east)),
          strokeColor: theme.palette.primary.light,
          strokeOpacity: 0.4,
          strokeWeight: 1,
          fillColor: theme.palette.primary.light,
          fillOpacity: 0.05,
          map: map,
          zIndex: 0
        });

        rectanglesRef.current.push(rectangle);
        tileCount++;
      }
    }

    // Cleanup function
    return () => {
      rectanglesRef.current.forEach((rect) => rect.setMap(null));
      rectanglesRef.current = [];
    };
  }, [map, visible, clusteringOptions, theme.palette.primary.light]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      rectanglesRef.current.forEach((rect) => rect.setMap(null));
      rectanglesRef.current = [];
    };
  }, []);

  return null; // This component doesn't render anything directly
};

export default ClusteringGridOverlay;
